'use client';

import type { ReactNode, SetStateAction } from 'react';
import type { User } from '@/types/user/type';
import type { ConclusionType } from '~/model/ConclusionType';
import type { InteractionType } from '~/model/InteractionType';
import { VpButton } from '@vtmn-play/react/headless';
import htmlReactParser from 'html-react-parser';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ProjectFollowProvider } from '@/app/[locale]/(entity)/components/provider/ProjectFollowProvider';
import { CrossSell } from '@/app/[locale]/(form)/components/CrossSell';
import { fetchItem } from '@/app/actions/server';
import { MainColumn } from '@/app/components/MainColumn';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { SignUpBanner } from '~/components/SignUpBanner/SignUpBanner';
import { InteractionTags } from '~/model/InteractionType';
import { isGuestUser, isMainUser } from '~/utils/user/user';
import { reactScrollConfigBumpy, scrollToTop } from '~/utils/utils';
import { userInformationModified } from '../Form/utils';
import { ContactInformation } from '../Selection/ContactInformation';
import { FollowedUpProjectBanner } from '../Thread/FollowedUpProjectBanner';
import { checkIsSelection } from '../utils';
import { ConclusionDefault } from './ConclusionDefault';

export const Conclusion = ({
  conclusion,
  translateButtonComponent,
  interaction,
  langValue,
  setIsLoading,
  onUserInformationUpdate,
}: {
  conclusion: ConclusionType;
  translateButtonComponent: ReactNode;
  interaction: InteractionType;
  langValue: string;
  setIsLoading: (value: SetStateAction<boolean>) => void;
  onUserInformationUpdate: () => Promise<void>;
}) => {
  const t = useTranslations();

  const { setError } = useApiErrorContext();
  const { user, setUser } = useAuthenticatedContext();

  const [phone, setPhone] = useState(user?.phone || '');
  const [zipcode, setZipcode] = useState(user?.zipcode || '');
  const [address, setAddress] = useState(user?.address || '');
  const [city, setCity] = useState(user?.city || '');

  const isUserInformationModified
    = !!user?.id
      && userInformationModified(
        user,
        interaction.tag === InteractionTags.PRODUCT_TEST_REMOTE,
        phone,
        city,
        zipcode,
        address,
      );

  const handleSubmitContactInformation = async () => {
    if (!isUserInformationModified) {
      return onUserInformationUpdate();
    }

    setIsLoading(true);

    const response = await fetchItem<User>({
      pathname: 'users/me',
      method: 'PUT',
      revalidate: 0,
      body: {
        address,
        city,
        phone,
        zipcode,
      },
    });

    if (response['hydra:description']) {
      setIsLoading(false);
      return setError(response);
    }

    setUser(response);
    onUserInformationUpdate();
    scrollToTop(reactScrollConfigBumpy);
    setIsLoading(false);
  };

  return (
    <>
      {conclusion?.id !== 0
        ? (
            <div className="conclusion" data-testid="conclusion">
              <BeardmanTalking
                title={
                  conclusion && <span lang={langValue}>{conclusion.title}</span>
                }
                description={
                  conclusion && (
                    <>
                      <span lang={langValue}>
                        {htmlReactParser(conclusion.body || '')}
                      </span>
                      <span>{translateButtonComponent}</span>
                    </>
                  )
                }
                isGuest={isGuestUser(user)}
                isConlusion={!!conclusion}
              >
                {conclusion?.media?.content_url && (
                  <div className="entity__media">
                    <div className="entity__picture">
                      <ResizedImg
                        src={conclusion.media.content_url}
                        format="1042x0"
                        width={1042}
                        height={200}
                        alt=""
                        loading="eager"
                      />
                    </div>
                  </div>
                )}
                {interaction && isMainUser(user) && (
                  <ProjectFollowProvider project={interaction.project}>
                    <FollowedUpProjectBanner inverse bottom />
                  </ProjectFollowProvider>
                )}
              </BeardmanTalking>
            </div>
          )
        : (
            <ConclusionDefault interaction={interaction} />
          )}
      {isGuestUser(user) && (
        <>
          <SignUpBanner />
          <CrossSell projectId={interaction?.project?.id} />
        </>
      )}
      {checkIsSelection(interaction) && isMainUser(user) && (
        <MainColumn column={journeyColumnTemplate}>
          <div className="form-contact__header">
            <h3 className="vp-body-l">{t('SELECTION.FORM_USER_DATA_LABEL')}</h3>
            <p className="vp-body-s timeslots-confirmation__message">
              {t('SELECTION.FORM_USER_DATA')}
            </p>
          </div>
          <ContactInformation
            address={address}
            atHome={interaction.tag === InteractionTags.PRODUCT_TEST_REMOTE}
            city={city}
            isClickSubmit={false}
            phone={phone}
            zipcode={zipcode}
            setPhone={setPhone}
            setCity={setCity}
            setAddress={setAddress}
            setZipcode={setZipcode}
          />
          <VpButton
            data-cy="contact-information-update"
            onClick={handleSubmitContactInformation}
          >
            {t('FORM.UPDATE_BUTTON')}
          </VpButton>
        </MainColumn>
      )}
    </>
  );
};
